(() => {
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  class LightSwipe {
    constructor(container, options) {
      this.container = container;
      this.current = options?.start ?? 0;
      this.extra = options?.extra ?? "none";
      this.onView = options?.onView;

      this.onNext = this.onNext.bind(this);
      this.onBack = this.onBack.bind(this);
      this.allBack = this.container.querySelectorAll("[data-back]");
      this.allSteps = Array.from(container.querySelectorAll(".step"));
      this.steps = this.allSteps.filter(
        (s) => !s.classList.contains("extra-step"),
      );
      this.stepsWrapper = container.querySelector(".steps");
      this.setupEvents();
      if (this.current !== 0)
        this.allBack.forEach((btn) => (btn.disabled = false));
      if (this.extra !== "none") this.setExtra(this.extra, false);
      else this.setActive(this.current, false);
      AOS && AOS.refresh();

      this.container.classList.add("transition");
    }

    setupEvents() {
      this.allBack.forEach((btn) => btn.addEventListener("click", this.onBack));
      this.container
        .querySelectorAll("[data-next]")
        .forEach((btn) => btn.addEventListener("click", this.onNext));
    }

    clearEvents() {
      this.allBack.forEach((btn) =>
        btn.removeEventListener("click", this.onBack),
      );
      this.container
        .querySelectorAll("[data-next]")
        .forEach((btn) => btn.removeEventListener("click", this.onNext));
    }

    currentStep() {
      return this.steps[this.current];
    }

    onNext() {
      if (this.current + 1 >= this.steps.length) {
        this.setExtra("final");
        return;
      }
      if (this.current === 0) {
        this.allBack.forEach((btn) => (btn.disabled = false));
      }
      this.setActive(++this.current);
    }

    onBack() {
      if (this.extra !== "none") {
        this.setActive(this.current);
        return;
      }
      if (this.current === 0) return;
      this.setActive(--this.current);
    }

    focusStep(step) {
      const target = step.querySelector("h2, h3") || step;
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    }

    setActive(index, moveFocus = true) {
      this.extra = "none";
      this.allSteps.forEach((step, idx) => {
        step.classList.remove("active", "prev", "next");
        if (idx < index) step.classList.add("prev");
        if (idx > index) step.classList.add("next");
      });
      const newStep = this.steps[index];
      newStep.classList.add("active");
      // this.stepsWrapper.style.height = newStep.offsetHeight + "px";

      if (index === 0) {
        this.allBack.forEach((btn) => (btn.disabled = true));
      }
      if (this.onView) this.onView(index, "none");
      if (moveFocus) this.focusStep(newStep);
    }

    setExtra(name, moveFocus = true) {
      this.extra = name;
      let extStep;
      this.allSteps.forEach((step, idx) => {
        step.classList.remove("active", "prev", "next");
        if (step.dataset.name === name) {
          extStep = step;
        } else {
          step.classList.add("prev");
        }
      });
      extStep.classList.add("active");
      if (this.onView) this.onView(this.current, name);
      if (moveFocus) this.focusStep(extStep);
    }
  }

  class OptionsForm {
    constructor(formElement) {
      this.form = formElement;

      this.onSubmit = this.onSubmit.bind(this);
      this.form.addEventListener("submit", this.onSubmit);
    }

    async onSubmit(evt) {
      evt.preventDefault();
      this.clearErrors();

      const formData = new FormData(this.form);
      let hasErrors = false;
      for (const [name, value] of formData.entries()) {
        const input = this.form.elements[name];
        const error = this.validateInput(input, value);
        if (error) {
          hasErrors = true;
          this.showError(input, error);
        }
      }

      if (!hasErrors) {
        try {
          this.form.elements["submit"]?.classList.add("btn--loading");

          const response = await fetch("/apps/jkcustom/repair-custom-options", {
            method: "POST",
            body: formData,
          });
          const json = await response.json();
          const errors = Object.entries(json.error?.fieldErrors || {});
          if (errors.length > 0) {
            errors.forEach(([key, value]) => {
              const input = this.form.elements[key];
              this.showError(input, value[0]);
            });
          } else if (response.status !== 200) {
            throw new Error("status error");
          } else {
            this.form.reset();
            const successNote = document.createElement("div");
            successNote.className = "note note--success";
            successNote.innerText =
              "Custom rebuild request submitted successfully, please allow 3-5 days for a response";
            this.form.prepend(successNote);
          }
        } catch (err) {
          console.log("error submitting", err);
          const errorNote = document.createElement("div");
          errorNote.className = "note form--error";
          errorNote.innerText = "Error submitting request, please try again";
          this.form.prepend(errorNote);
        } finally {
          this.form.elements["submit"]?.classList.remove("btn--loading");
        }
      }
    }

    validateInput(input, value) {
      if (input.required && !value.trim()) {
        return "This field is required";
      }
      if (input.type === "number" && input.hasAttribute("min")) {
        const min = Number(input.min);
        const numVal = Number(value);
        if (numVal < min) {
          return `Value must be greater than ${min}`;
        }
      }
      return null;
    }

    showError(input, message) {
      input.setAttribute("aria-invalid", "true");
      const error = document.createElement("small");
      error.className = "error";
      error.textContent = message;
      input.insertAdjacentElement("afterend", error);
    }

    clearErrors() {
      this.form.querySelectorAll(".error").forEach((el) => el.remove());
      this.form
        .querySelectorAll("[aria-invalid]")
        .forEach((el) => el.removeAttribute("aria-invalid"));
    }

    clearEvents() {
      this.form.removeEventListener("submit", this.onSubmit);
    }
  }

  if (!customElements.get("light-steps")) {
    customElements.define(
      "light-steps",
      class LightSteps extends HTMLElement {
        constructor() {
          super();

          this.initModals = this.initModals.bind(this);
          this.setExtra = this.setExtra.bind(this);
        }
        connectedCallback() {
          this.querySelectorAll("[data-answer]").forEach((btn) =>
            btn.addEventListener("click", this.setExtra),
          );
          if (theme.Modals) {
            this.initModals();
          } else {
            document.addEventListener("page:loaded", this.initModals);
          }
          this.swiper = new LightSwipe(this);
          AOS.refresh();
        }
        disconnectedCallback() {
          this.querySelectorAll("[data-answer]").forEach((btn) =>
            btn.removeEventListener("click", this.setExtra),
          );
          document.removeEventListener("page:loaded", this.initModals);
        }

        initModals() {
          new theme.Modals(`LightStepsTestModal`, `light-steps-modal`);
          document.removeEventListener("page:loaded", this.initModals);
        }

        setExtra(evt) {
          const answer = evt.currentTarget.dataset.answer;
          if (answer === "reject") {
            this.swiper.setExtra("reject");
          }
        }
      },
    );
  }

  const timedStorage = {
    setItem(key, value, maxAge = 5 * 60 * 1000) {
      localStorage.setItem(
        `ts-${key}`,
        JSON.stringify({
          value,
          expireTime: Date.now() + maxAge,
        }),
      );
    },

    getItem(key) {
      const result = JSON.parse(localStorage.getItem(`ts-${key}`));
      if (result) {
        if (result.expireTime && result.expireTime <= Date.now()) {
          localStorage.removeItem(`ts-${key}`);
          return null;
        }
        return result.value;
      }
      return null;
    },

    removeItem(key) {
      localStorage.removeItem(`ts-${key}`);
    },
  };

  if (!customElements.get("step-images")) {
    customElements.define(
      "step-images",
      class StepImages extends HTMLElement {
        connectedCallback() {
          this.images = this.querySelectorAll(".main-image");
          this.thumbs = this.querySelectorAll(".thumbs button");

          this._onThumbClickHandler = this.onThumbClick.bind(this);
          this.thumbs.forEach((button) => {
            button.addEventListener("click", this._onThumbClickHandler);
          });
        }

        disconnectedCallback() {
          this.thumbs.forEach((button) => {
            button.removeEventListener("click", this._onThumbClick);
          });
        }

        onThumbClick(evt) {
          const btn = evt.currentTarget;
          btn.classList.add("active");

          this.thumbs.forEach((thumb) => {
            thumb.classList.toggle(
              "active",
              thumb.dataset.target === btn.dataset.target,
            );
          });

          this.images.forEach((img) => {
            img.classList.toggle(
              "hide",
              !img.classList.contains(btn.dataset.target),
            );
          });
        }
      },
    );
  }

  if (!customElements.get("repairs-wizard")) {
    customElements.define(
      "repairs-wizard",
      class RepairsWizard extends HTMLElement {
        currentStep = 0;
        extraStep = "none";
        answers = {};
        #storeKey = "repwiz-data";

        constructor() {
          super();

          this.#storeKey = `repwiz-${this.dataset.type.toLowerCase()}-data`;
          const storedData = timedStorage.getItem(this.#storeKey);
          if (storedData?.currentStep && !isNaN(storedData.currentStep)) {
            this.currentStep = Number(storedData?.currentStep);
          }
          if (storedData) {
            this.answers = storedData.answers;
            this.extraStep = storedData.extraStep;
          }

          this.initModals = this.initModals.bind(this);
        }

        connectedCallback() {
          this.cancelLink = this.querySelector(".cancel-link");
          this.productView = this.querySelector(".product-view");

          const optFormElement = this.querySelector("#rwcoForm");
          if (optFormElement) {
            this.optionsForm = new OptionsForm(optFormElement);
          }

          this.initEvents();

          this.swiper = new LightSwipe(this, {
            start: this.currentStep,
            extra: this.extraStep,
            onView: this.#setView.bind(this),
          });
          if (this.extraStep === "final") {
            this.handleFinal(false);
          }

          if (theme.Modals) {
            this.initModals();
          } else {
            document.addEventListener("page:loaded", this.initModals);
          }

          this.classList.remove("unload");
        }

        disconnectedCallback() {
          this.swiper.clearEvents();
          this.removeEventListener("input", this._onValidateHandler);
          this.querySelectorAll("[data-answer]").forEach((btn) => {
            btn.removeEventListener("click", this._onChoiceClickHandler);
          });
          this.cancelLink.removeEventListener("click", this._cancelHandler);
          this.optionsForm?.clearEvents();
        }

        initModals() {
          const modalsContainer = this.querySelector(".service-wiz-modals");
          this.swiper.allSteps.forEach((step) => {
            if (step.dataset.helpModal === "true") {
              const modal = step.querySelector(
                `#RepWizHelpModal-${step.dataset.blockId}`,
              );
              modal.remove();
              modalsContainer.appendChild(modal);
              new theme.Modals(
                `RepWizHelpModal-${step.dataset.blockId}`,
                `help-modal-${step.dataset.blockId}`,
              );
            }
          });
          document.removeEventListener("page:loaded", this.initModals);
        }

        initEvents() {
          this._onValidateHandler = this.validateStep.bind(this);
          this.addEventListener("input", this._onValidateHandler);

          this._onChoiceClickHandler = this.onChoiceClick.bind(this);
          this.querySelectorAll("[data-answer]").forEach((btn) => {
            btn.addEventListener("click", this._onChoiceClickHandler);
          });

          this._cancelHandler = this.onCancel.bind(this);
          this.cancelLink.addEventListener("click", this._cancelHandler);
        }

        onCancel() {
          timedStorage.removeItem(this.#storeKey);
        }

        validateStep() {
          const step = this.swiper.currentStep();
          if (!step) {
            return;
          }

          if (step.classList.contains("checkbox-step")) {
            const checked = step.querySelectorAll(
              'input[type="checkbox"]:checked, input[type="radio"]:checked',
            );
            step.querySelector("[data-next]").disabled = checked.length === 0;
          }
        }

        onChoiceClick(evt) {
          const answer = evt.target.dataset.answer;
          const action = evt.target.dataset.action;
          const step = this.swiper.currentStep();

          this.answers[step.dataset.step] = { answer, action };
          this.#setAnswers(this.answers);

          if (action === "auto_reject") {
            this.swiper.setExtra("reject");
          } else if (action === "other_product") {
            this.swiper.setExtra("otherProduct");
          } else if (action === "custom_options_form") {
            this.swiper.setExtra("optionsForm");
          } else if (step.dataset.final === "true") {
            this.swiper.setExtra("final");
            this.handleFinal(true);
          } else if (
            action === "resole_assessment" ||
            action === "rebuild_assessment"
          ) {
            const url =
              action === "resole_assessment"
                ? `${theme.routes.home}/pages/resole-assessment`.replace(
                    "//",
                    "/",
                  )
                : `${theme.routes.home}/pages/rebuild-assessment`.replace(
                    "//",
                    "/",
                  );
            timedStorage.removeItem(
              `repwiz-${
                action === "resole_assessment" ? "resole" : "rebuild"
              }-data`,
            );
            window.location.href = url;
          } else {
            this.swiper.onNext();
          }
        }

        async handleFinal(showLoader) {
          if (showLoader) {
            const loadingTemplate = this.querySelector(".loading-markup");
            this.productView.innerHTML = "";
            this.productView.appendChild(
              document.importNode(loadingTemplate.content, true),
            );
            await sleep(Math.floor(Math.random() * (2500 - 1000 + 1)) + 1000);
          }

          const answerList = Object.values(this.answers);
          let product = this.dataset.type;
          let recText = "Qualified";
          let explanation = "";

          if (
            this.dataset.type === "Rebuild" &&
            answerList.some((a) => a.action === "resole_product")
          ) {
            recText = "Recommended";
            explanation =
              "Based on your boot condition, we recommend a resole instead of a rebuild for your boots.";
            product = "Resole";
          } else if (
            this.dataset.type === "Resole" &&
            answerList.some((a) => a.action === "rebuild_product")
          ) {
            product = "Rebuild";
            recText = "Recommended";
            explanation =
              "Based on your boot condition, we recommend a full rebuild for your boots.";
          } else if (this.dataset.type === "Resole") {
            product = "Resole";
            recText = "Qualified";
            explanation =
              "Your boot qualifies for a resole, continue to select your resole options.";
          } else if (this.dataset.type === "Rebuild") {
            product = "Rebuild";
            recText = "Qualified";
            explanation =
              "Your boot qualifies for a rebuild, continue to select your rebuild options.";
          }

          const template = this.querySelector(`#repWiz${product}Product`);
          const clone = document.importNode(template.content, true);
          clone.querySelector(".rec-text").innerHTML = recText;
          clone.querySelector(".ex-text").innerHTML = explanation;
          this.productView.innerHTML = "";
          this.productView.appendChild(clone);
          if (showLoader) this.swiper.focusStep(this.productView);
        }

        #setView(current, extra) {
          this.currentStep = current;
          this.extraStep = extra;
          this.#storeFields();
        }

        #setAnswers(answers) {
          this.answers = answers;
          this.#storeFields();
        }

        #storeFields() {
          timedStorage.setItem(this.#storeKey, {
            currentStep: this.currentStep,
            extraStep: this.extraStep,
            answers: this.answers,
          });
        }
      },
    );
  }
})();
